import { Injectable } from '@angular/core';
import { AssetType } from '@modules/cards/types/asset.types';
import { TagMenu } from '@modules/shared/schemas/tag.schema';
import { BehaviorSubject } from 'rxjs';

const emptyFilteredTags: { [key in AssetType]: TagMenu[] } = {
  DOCUMENT: [],
  IMAGE: [],
  MEDIA_COLLECTION: [],
  MEDIA_SPECIAL: [],
  PRESS_INFORMATION: [],
  PRESS_KIT: [],
  VIDEO: [],
  AUDIO: [],
  EVENT: [],
  PRESS_CONTACT: [],
  TECHNICAL_DATA: [],
};

const emptyActiveTags: { [key in AssetType]: number[] } = {
  DOCUMENT: [],
  IMAGE: [],
  MEDIA_COLLECTION: [],
  MEDIA_SPECIAL: [],
  PRESS_INFORMATION: [],
  PRESS_KIT: [],
  VIDEO: [],
  AUDIO: [],
  EVENT: [],
  PRESS_CONTACT: [],
  TECHNICAL_DATA: [],
};
@Injectable({
  providedIn: 'root',
})
export class FilterService {
  private _toggleFilterLvl$ = new BehaviorSubject<boolean>(false);

  private _filteredTagsHelper: { [key: string]: TagMenu[] } = {};
  private _activeTagsHelper: { [key: string]: number[] } = {};
  private _availableTagsHelper: { [key: string]: number[] } = {};

  private _filteredTags$ = new BehaviorSubject<{ [key in AssetType]?: TagMenu[] }>({});
  private _activeTags$ = new BehaviorSubject<{ [key: string]: number[] }>({});
  private _availableTags$ = new BehaviorSubject<{ [key: string]: number[] }>({});

  public filteredTags$ = this._filteredTags$.asObservable();

  /**
   * Observable subject for active tags list
   * Active tags list is list of tag Ids that are available for specific topic after some tags are already filtered/checked
   */
  public activeTags$ = this._activeTags$.asObservable();

  /**
   * Observable subject for available tags list
   * Available tag list is list of all tag Ids that are available for specific topic
   */
  public availableTags$ = this._availableTags$.asObservable();

  public toggleFilterLvl$ = this._toggleFilterLvl$.asObservable();

  constructor() {
    this._filteredTagsHelper = { ...emptyFilteredTags };
    this._filteredTags$.next(this._filteredTagsHelper);
    this._activeTagsHelper = { ...emptyActiveTags };
    this._activeTags$.next(this._activeTagsHelper);
    this._availableTagsHelper = { ...emptyActiveTags };
    this._availableTags$.next(this._availableTagsHelper);
  }

  public addTag(tag: TagMenu, type: AssetType) {
    this._filteredTagsHelper[type].push(tag);
    this._filteredTags$.next(this._filteredTagsHelper);
  }

  public removeTag(tag: TagMenu, type: AssetType) {
    this._filteredTagsHelper[type] = this._filteredTagsHelper[type].filter((item) => item.id !== tag.id);
    this._filteredTags$.next(this._filteredTagsHelper);
  }

  public resetTagMenu(parentTagId: number, type: AssetType) {
    this._filteredTagsHelper[type] = this._filteredTagsHelper[type].filter(
      (item) => !item.parents.includes(parentTagId.toString())
    );
    this._filteredTags$.next(this._filteredTagsHelper);
  }

  public set(tags: TagMenu[], type: AssetType) {
    this._filteredTagsHelper[type] = tags;
    this._filteredTags$.next(this._filteredTagsHelper);
  }

  public reset(type: AssetType) {
    this._filteredTagsHelper[type] = [];
    this._filteredTags$.next(this._filteredTagsHelper);
  }

  public resetAll() {
    for (let key in this._filteredTagsHelper) {
      this._filteredTagsHelper[key] = [];
    }
    this._filteredTags$.next(this._filteredTagsHelper);

    for (let key in this._activeTagsHelper) {
      this._activeTagsHelper[key] = [];
    }
    this._activeTags$.next(this._activeTagsHelper);
  }

  public changeActiveTags(tagIds: number[], type: AssetType) {
    this._activeTagsHelper[type] = tagIds;
    this._activeTags$.next(this._activeTagsHelper);
  }

  public changeAvailableTags(tagIds: number[], type: AssetType) {
    this._availableTagsHelper[type] = tagIds;
    this._availableTags$.next(this._availableTagsHelper);
  }

  public openFirstFilterLvl() {
    this._toggleFilterLvl$.next(false);
  }

  public openSecondFilterLvl() {
    this._toggleFilterLvl$.next(true);
  }
}
